import { Icon } from '@acme/assets';
import { ResultModal, useModal } from '@acme/foundation';
import {
  Button,
  Dropdown,
  DropdownDivider,
  DropdownItem,
} from 'flowbite-react';
import { DeleteRateModal, ExtendRateModal } from './modal';
import { ExchangeRateForm } from './exchangeRateForm';
import { ClientExchangeRateResponse } from '@acme/domains';
import { ExchanegeRateService } from '@acme/api';
import { useNavigate } from 'react-router-dom';

type Period = '24h' | '72h' | '1w';

const timePeriods: { value: Period; label: string }[] = [
  { value: '24h', label: '24 часа' },
  { value: '72h', label: '3 дня' },
  { value: '1w', label: '1 неделя' },
];

const periodToMs = {
  '24h': 24 * 60 * 60 * 1000,
  '72h': 72 * 60 * 60 * 1000,
  '1w': 7 * 24 * 60 * 60 * 1000,
};

const addPeriodToDate = (date: Date, period: Period) => {
  const ms = periodToMs[period];
  return new Date(date.getTime() + ms).toISOString();
};
const parseDate = (dateString: string): Date => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    throw new Error(`Неверный формат даты: ${dateString}`);
  }
  return date;
};

interface IActionMenuButton {
  rowData: ClientExchangeRateResponse;
  reload?: () => void;
}

export const ActionMenuButton = (props: IActionMenuButton) => {
  const { openModal, openConfirmModal, closeModal } = useModal();
  const navigate = useNavigate();

  const onDeleteRate = () => {
    ExchanegeRateService.deleteApiV1ExchanegeRateRemove({
      requestBody: { rateId: props.rowData.rateId },
    }).then((res) => {
      closeModal();
      openModal({
        content: (
          <ResultModal
            title={res.isSuccess ? 'Успешно' : 'Удалить курс не удалось'}
            description={
              res.isSuccess
                ? 'Курс валюты был удалён'
                : 'Попробуйте повторить позднее'
            }
            type={res.isSuccess ? 'success' : 'error'}
            onBack={() => {
              navigate('/rates');
              closeModal();
              props.reload?.();
            }}
          />
        ),
        opaqueBackground: true,
      });
    });
  };

  const handleDeleteClick = async () => {
    try {
      openModal({
        content: (
          <DeleteRateModal
            label="Удалить курс валюты из списка?"
            onClose={() => {
              closeModal();
              props.reload?.();
            }}
            onApprove={onDeleteRate}
          />
        ),
        modalType: 'custom',
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handlePreferredPeriodClick = async (period: Period) => {
    try {
      const confirmed = await openConfirmModal({
        title: 'Продлить действие курса',
        content: 'Действие курса будет продлено на выбранное время',
        okBtn: 'Подтвердить',
        cancelBtn: 'Отмена',
      });

      if (!confirmed) return;
      const expireAt = addPeriodToDate(
        parseDate(props.rowData.expireAt as string),
        period
      );
      ExchanegeRateService.putApiV1ExchanegeRateExtend({
        requestBody: { rateId: props.rowData.rateId, expireAt },
      }).then((res) => {
        closeModal();
        openModal({
          content: (
            <ResultModal
              title={res.isSuccess ? 'Успешно' : 'Продлить курс не удалось'}
              description={
                res.isSuccess
                  ? 'Курс валюты был продлен'
                  : 'Попробуйте повторить позднее'
              }
              type={res.isSuccess ? 'success' : 'error'}
              onBack={() => {
                navigate('/rates');
                closeModal();
                props.reload?.();
              }}
            />
          ),
          opaqueBackground: true,
        });
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleChangeRateClick = () => {
    openModal({
      content: (
        <ExchangeRateForm
          onClose={closeModal}
          defaultData={props.rowData}
          onSubmit={ExchanegeRateService.putApiV1ExchanegeRateUpdateProvider}
        />
      ),
      size: 'xl',
      modalType: 'custom',
    });
  };

  const onApproveCustomPeriod = (expireAt: Date | null) => {
    ExchanegeRateService.putApiV1ExchanegeRateExtend({
      requestBody: {
        rateId: props.rowData.rateId,
        expireAt: expireAt?.toISOString(),
      },
    }).then((res) => {
      closeModal();
      openModal({
        content: (
          <ResultModal
            title={res.isSuccess ? 'Успешно' : 'Продлить курс не удалось'}
            description={
              res.isSuccess
                ? 'Курс валюты был продлен'
                : 'Попробуйте повторить позднее'
            }
            type={res.isSuccess ? 'success' : 'error'}
            onBack={() => {
              navigate('/rates');
              closeModal();
              props.reload?.();
            }}
          />
        ),
        opaqueBackground: true,
      });
    });
  };

  const handleCustomPreriodClick = () => {
    try {
      openModal({
        content: (
          <ExtendRateModal
            onApprove={onApproveCustomPeriod}
            onClose={() => {
              closeModal();
              props.reload?.();
            }}
          />
        ),
        modalType: 'custom',
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Dropdown
      label=""
      dismissOnClick={false}
      placement="right-start"
      renderTrigger={() => (
        <Button size="sm" color="gray" className="px-2 bg-transparent">
          <Icon name="dots-horizontal-outline" />
        </Button>
      )}
    >
      <DropdownItem
        className="text-sm font-medium text-gray-900"
        onClick={handleChangeRateClick}
      >
        Изменить курс
      </DropdownItem>
      <DropdownItem className="py-0 text-sm font-medium text-gray-900">
        <Dropdown
          label=""
          dismissOnClick={false}
          placement="right-start"
          renderTrigger={() => (
            <Button
              color="gray"
              className="px-0  bg-transparent hover:bg-transparent"
            >
              <div className="flex gap-1.5 items-center w-full">
                <span>Продлить курс</span>
                <div className="flex gap-2 py-0.5 px-2.5 rounded-full bg-green-100 items-center">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-xs text-green-800">24 ч</span>
                </div>
              </div>
              <Icon name="chevron-right" color="#111928" className="ml-auto" />
            </Button>
          )}
        >
          {timePeriods.map((period, index) => (
            <DropdownItem
              key={index}
              className="text-sm font-medium text-gray-900"
              onClick={() => handlePreferredPeriodClick(period.value)}
            >
              {period.label}
            </DropdownItem>
          ))}

          <DropdownItem
            className="text-sm font-medium text-gray-900"
            onClick={handleCustomPreriodClick}
          >
            Задать время
          </DropdownItem>
        </Dropdown>
      </DropdownItem>
      <DropdownDivider />
      <DropdownItem
        onClick={handleDeleteClick}
        className="text-red-600 hover:text-red-800 hover:bg-red-50"
      >
        Удалить из списка
      </DropdownItem>
    </Dropdown>
  );
};
